import type { Artist } from '../types';

/**
 * Utility functions for handling multiple artists in songs
 */

/**
 * Parses artistId field which can contain a single ID or comma-separated multiple IDs
 * @param artistId - The artistId field from a song (e.g., "1" or "1,2,3")
 * @returns Array of individual artist IDs
 */
export function parseArtistIds(artistId: string): string[] {
  if (!artistId) return [];
  return artistId.split(',').map(id => id.trim()).filter(id => id.length > 0);
}

/**
 * Gets artist objects from an array of artist IDs
 * @param artistIds - Array of artist IDs
 * @param allArtists - Array of all available artists
 * @returns Array of artist objects that were found
 */
export function getArtistsFromIds(artistIds: string[], allArtists: Artist[]): Artist[] {
  return artistIds
    .map(id => allArtists.find(artist => artist.id === id))
    .filter((artist): artist is Artist => artist !== undefined);
}

/**
 * Gets artist names from artistId field (handles both single and multiple artists)
 * @param artistId - The artistId field from a song (e.g., "1" or "1,2,3")
 * @param allArtists - Array of all available artists
 * @returns String of artist names (e.g., "Artist Name" or "Artist1, Artist2, Artist3")
 */
export function getArtistNames(artistId: string, allArtists: Artist[]): string {
  const artistIds = parseArtistIds(artistId);
  const artists = getArtistsFromIds(artistIds, allArtists);
  
  if (artists.length === 0) {
    return 'Unknown Artist';
  }
  
  return artists.map(artist => artist.name).join(', ');
}

/**
 * Gets primary artist from artistId field (first artist for multi-artist songs)
 * @param artistId - The artistId field from a song (e.g., "1" or "1,2,3")
 * @param allArtists - Array of all available artists
 * @returns Primary artist object or null if not found
 */
export function getPrimaryArtist(artistId: string, allArtists: Artist[]): Artist | null {
  const artistIds = parseArtistIds(artistId);
  if (artistIds.length === 0) return null;
  
  const primaryArtist = allArtists.find(artist => artist.id === artistIds[0]);
  return primaryArtist || null;
}

/**
 * Checks if a song has multiple artists
 * @param artistId - The artistId field from a song
 * @returns true if the song has multiple artists, false otherwise
 */
export function hasMultipleArtists(artistId: string): boolean {
  return parseArtistIds(artistId).length > 1;
}

/**
 * Gets all artist IDs that should be considered when filtering songs by artist
 * @param artistId - The artistId field from a song
 * @param targetArtistId - The artist ID we're looking for
 * @returns true if the song belongs to the target artist (including collaborations)
 */
export function songBelongsToArtist(artistId: string, targetArtistId: string): boolean {
  const artistIds = parseArtistIds(artistId);
  return artistIds.includes(targetArtistId);
}
